// src/pages/Home.jsx
// Adjust the file path/name if your Home component is somewhere else.

import { useEffect, useState } from "react";

const PURPLE = "#673ab7";

export default function Home() {
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    /*
     * FormFill saves payment details in sessionStorage
     * before redirecting here.
     */
    try {
      const params = new URLSearchParams(window.location.search);
      const showPayment = params.get("showPayment");

      const savedPayment = sessionStorage.getItem("pendingPaymentInstructions");

      if (showPayment === "true" && savedPayment) {
        const parsed = JSON.parse(savedPayment);

        setPaymentData(parsed);

        /*
         * Remove ?showPayment=true from URL
         * without refreshing the page.
         */
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    } catch (error) {
      console.error("Could not load payment instructions:", error);
    }
  }, []);

  /* =========================================================
     CLOSE PAYMENT BOX
  ========================================================= */

  const closePayment = () => {
    /*
     * Clear saved payment details only when
     * the user closes the payment box.
     */
    sessionStorage.removeItem("pendingPaymentInstructions");

    setPaymentData(null);
  };

  /* =========================================================
     COPY
  ========================================================= */

  const copyText = async (text) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(String(text));

      setCopied(String(text));

      setTimeout(() => {
        setCopied("");
      }, 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <>
      <HomeStyles />

      <div className="home-page">
        {/* =============================================
            NORMAL HOME PAGE
        ============================================= */}

        <div className="home-container">
          <div className="banner-grid">
            <div className="banner-card">
              <img
                src="/banner 2.jpeg"
                alt="Ekopack Products"
                className="home-banner"
              />
            </div>

            <div className="banner-card">
              <img
                src="/banner 1.jpeg"
                alt="Rupioo Face HRM"
                className="home-banner"
              />
            </div>
          </div>
        </div>

        {/* =============================================
            PAYMENT INSTRUCTIONS DIALOG
        ============================================= */}

        {paymentData ? (
          <div className="payment-overlay">
            <div className="payment-dialog">
              {/* HEADER */}

              <div className="payment-header">
                <div>
                  <h2>Payment Instructions</h2>

                  {paymentData.formTitle ? (
                    <p>{paymentData.formTitle}</p>
                  ) : null}
                </div>

                <button
                  className="close-button"
                  onClick={closePayment}
                  type="button"
                >
                  ×
                </button>
              </div>

              {/* SUCCESS MESSAGE */}

              <div className="response-success">
                <div className="success-icon">✓</div>

                <div>
                  <strong>Your response has been saved</strong>

                  <p>Please complete the payment using the details below.</p>
                </div>
              </div>

              {/* PAYMENT METHODS */}

              <div className="payment-body">
                {(paymentData.payments || []).map((payment, index) => (
                  <PaymentDetails
                    key={payment.id || index}
                    payment={payment}
                    index={index}
                    copied={copied}
                    copyText={copyText}
                  />
                ))}
              </div>

              {/* FOOTER */}

              <div className="payment-footer">
                <button
                  type="button"
                  className="done-button"
                  onClick={closePayment}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/* =========================================================
   PAYMENT DETAILS
========================================================= */

function PaymentDetails({ payment, index, copied, copyText }) {
  const config = payment?.paymentConfig || {};
  const method = config.method;
  console.log(config.qrImageUrl);
  const API_BASE = "https://textshare-node.rupioo.com";
  const qrImageSource = config?.qrImageUrl?.includes("/uploads/")
    ? `${API_BASE.replace(/\/+$/, "")}${config.qrImageUrl.slice(
        config.qrImageUrl.indexOf("/uploads/"),
      )}`
    : config?.qrImageUrl || "";

  return (
    <div className="payment-method">
      {index > 0 ? <div className="payment-separator" /> : null}
      {payment.title ? (
        <div className="payment-title">{payment.title}</div>
      ) : null}
      {/* =============================================
          QR CODE
      ============================================= */}
      {method === "qr_code" ? (
        <div className="qr-section">
          {qrImageSource ? (
            <>
              <div className="qr-image-box">
                <img src={qrImageSource} alt="Payment QR Code" />
              </div>

              <p className="qr-help">
                Scan this QR code using your payment app.
              </p>
            </>
          ) : (
            <p className="empty-payment">Payment QR code is unavailable.</p>
          )}

          {config.instructions ? (
            <InstructionBox>{config.instructions}</InstructionBox>
          ) : null}
        </div>
      ) : null}
      {/* =============================================
          PAYMENT LINK
      ============================================= */}
      {method === "payment_link" ? (
        <div>
          {config.instructions ? (
            <InstructionBox>{config.instructions}</InstructionBox>
          ) : null}

          {isValidHttpUrl(config.paymentUrl) ? (
            <button
              type="button"
              className="payment-link-button"
              onClick={() => {
                window.open(config.paymentUrl, "_blank", "noopener,noreferrer");
              }}
            >
              Open Payment Link
            </button>
          ) : (
            <p className="empty-payment">Payment link is unavailable.</p>
          )}
        </div>
      ) : null}
      {/* =============================================
          BANK TRANSFER
      ============================================= */}
      {method === "bank_transfer" ? (
        <div className="bank-section">
          <PaymentRow
            label="Account Holder"
            value={config.accountHolder}
            copied={copied}
            copyText={copyText}
          />

          <PaymentRow
            label="Bank Name"
            value={config.bankName}
            copied={copied}
            copyText={copyText}
          />

          <PaymentRow
            label="Account Number"
            value={config.accountNumber}
            copied={copied}
            copyText={copyText}
          />

          <PaymentRow
            label="IFSC Code"
            value={config.ifsc}
            copied={copied}
            copyText={copyText}
          />

          <PaymentRow
            label="Branch"
            value={config.branch}
            copied={copied}
            copyText={copyText}
          />

          {config.instructions ? (
            <InstructionBox>{config.instructions}</InstructionBox>
          ) : null}
        </div>
      ) : null}
      {/* =============================================
          UPI
      ============================================= */}
      {method === "upi_id" ? (
        <div>
          <div className="upi-box">
            <div>
              {config.payeeName ? (
                <span className="detail-label">Payee Name</span>
              ) : null}

              {config.payeeName ? (
                <div className="payee-name">{config.payeeName}</div>
              ) : null}

              <span className="detail-label">UPI ID</span>

              <div className="upi-id">
                {config.upiId || "UPI ID unavailable"}
              </div>
            </div>

            {config.upiId ? (
              <button
                type="button"
                className="copy-button"
                onClick={() => copyText(config.upiId)}
              >
                {copied === String(config.upiId) ? "Copied" : "Copy"}
              </button>
            ) : null}
          </div>

          {config.instructions ? (
            <InstructionBox>{config.instructions}</InstructionBox>
          ) : null}
        </div>
      ) : null}
      {/* =============================================
          ONLY INSTRUCTIONS
      ============================================= */}
      {method === "instructions" ? (
        <InstructionBox>
          {config.instructions ||
            "Please follow the provided payment instructions."}
        </InstructionBox>
      ) : null}
    </div>
  );
}

/* =========================================================
   PAYMENT ROW
========================================================= */

function PaymentRow({ label, value, copied, copyText }) {
  if (!value) return null;

  return (
    <div className="payment-row">
      <div>
        <span className="detail-label">{label}</span>

        <div className="detail-value">{value}</div>
      </div>

      <button
        type="button"
        className="copy-button"
        onClick={() => copyText(value)}
      >
        {copied === String(value) ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/* =========================================================
   INSTRUCTION BOX
========================================================= */

function InstructionBox({ children }) {
  return <div className="instruction-box">{children}</div>;
}

/* =========================================================
   URL VALIDATION
========================================================= */

function isValidHttpUrl(value) {
  try {
    const url = new URL(value);

    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/* =========================================================
   STYLES
========================================================= */

function HomeStyles() {
  return (
    <style>{`
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        font-family:
          Arial,
          Helvetica,
          sans-serif;
      }

      html,
      body,
      #root {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
      }

      .home-page {
        width: 100%;
        min-height: 100vh;
        background: #f0ebf8;
        color: #202124;
        overflow-x: hidden;
      }

      .home-container {
        width: 100%;
        max-width: 1600px;
        margin: 0 auto;
        padding: 16px;
      }

      .banner-grid {
        width: 100%;
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: clamp(8px, 1.5vw, 22px);
        align-items: start;
      }

      .banner-card {
        width: 100%;
        min-width: 0;
        overflow: hidden;
        border-radius: 12px;
        background: white;
        box-shadow: 0 4px 16px rgba(60, 64, 67, 0.14);
      }

      .home-banner {
        display: block;
        width: 100%;
        max-width: 100%;
        height: auto;
        object-fit: contain;
      }

      /* ==========================
         OVERLAY
      ========================== */

      .payment-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;

        background:
          rgba(32,33,36,0.55);

        display: flex;
        align-items: center;
        justify-content: center;

        padding: 16px;
      }

      /* ==========================
         DIALOG
      ========================== */

      .payment-dialog {
        width: 100%;
        max-width: 540px;
        max-height: 92vh;

        background: white;

        border-radius: 8px;

        box-shadow:
          0 8px 28px
          rgba(60,64,67,0.35);

        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      /* ==========================
         HEADER
      ========================== */

      .payment-header {
        padding: 20px 24px;

        border-top:
          8px solid ${PURPLE};

        border-bottom:
          1px solid #dadce0;

        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 15px;
      }

      .payment-header h2 {
        margin: 0;

        font-size: 22px;
        font-weight: 400;

        color: #202124;
      }

      .payment-header p {
        margin: 6px 0 0;

        color: #5f6368;

        font-size: 13px;
      }

      .close-button {
        border: none;
        background: transparent;

        color: #5f6368;

        font-size: 28px;
        line-height: 1;

        cursor: pointer;
      }

      /* ==========================
         SUCCESS
      ========================== */

      .response-success {
        margin: 16px 24px 0;

        padding: 14px;

        background: #e6f4ea;

        border-radius: 6px;

        display: flex;
        align-items: center;
        gap: 12px;
      }

      .success-icon {
        width: 32px;
        height: 32px;

        flex-shrink: 0;

        border-radius: 50%;

        background: #188038;
        color: white;

        display: flex;
        align-items: center;
        justify-content: center;

        font-weight: bold;
      }

      .response-success strong {
        display: block;

        font-size: 14px;

        color: #137333;
      }

      .response-success p {
        margin: 4px 0 0;

        font-size: 12px;

        color: #3c4043;
      }

      /* ==========================
         BODY
      ========================== */

      .payment-body {
        padding: 20px 24px;

        overflow-y: auto;
      }

      .payment-title {
        margin-bottom: 14px;

        font-size: 16px;
        font-weight: 500;
      }

      .payment-separator {
        height: 1px;

        background: #dadce0;

        margin: 24px 0;
      }

      /* ==========================
         QR
      ========================== */

      .qr-section {
        text-align: center;
      }

      .qr-image-box {
        display: inline-flex;

        padding: 10px;

        border:
          1px solid #dadce0;

        border-radius: 6px;

        background: white;
      }

      .qr-image-box img {
        width: 220px;
        max-width: 100%;
        max-height: 280px;

        display: block;

        object-fit: contain;
      }

      .qr-help {
        margin: 10px 0 0;

        color: #5f6368;

        font-size: 12px;
      }

      /* ==========================
         INSTRUCTION
      ========================== */

      .instruction-box {
        margin-top: 14px;

        padding: 13px 15px;

        background: #f8f9fa;

        border-radius: 4px;

        color: #3c4043;

        font-size: 13px;
        line-height: 1.6;

        text-align: left;

        white-space: pre-wrap;
      }

      /* ==========================
         PAYMENT LINK
      ========================== */

      .payment-link-button {
        width: 100%;

        min-height: 40px;

        margin-top: 14px;

        border: none;

        border-radius: 4px;

        background: ${PURPLE};

        color: white;

        padding: 0 20px;

        font-size: 14px;
        font-weight: 500;

        cursor: pointer;
      }

      .payment-link-button:hover {
        background: #5e35b1;
      }

      /* ==========================
         BANK
      ========================== */

      .bank-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .payment-row {
        min-height: 58px;

        padding: 10px 12px;

        border:
          1px solid #dadce0;

        border-radius: 4px;

        display: flex;
        align-items: center;
        justify-content: space-between;

        gap: 12px;
      }

      .detail-label {
        display: block;

        margin-bottom: 3px;

        color: #5f6368;

        font-size: 11px;
      }

      .detail-value {
        color: #202124;

        font-size: 14px;

        word-break: break-word;
      }

      /* ==========================
         COPY
      ========================== */

      .copy-button {
        flex-shrink: 0;

        border:
          1px solid #dadce0;

        border-radius: 4px;

        background: white;

        color: ${PURPLE};

        padding: 6px 10px;

        font-size: 11px;
        font-weight: 500;

        cursor: pointer;
      }

      /* ==========================
         UPI
      ========================== */

      .upi-box {
        padding: 14px;

        border:
          1px solid #dadce0;

        border-radius: 4px;

        display: flex;
        align-items: center;
        justify-content: space-between;

        gap: 14px;
      }

      .payee-name {
        margin-bottom: 10px;

        font-size: 14px;
      }

      .upi-id {
        font-size: 15px;
        font-weight: 500;

        word-break: break-all;
      }

      /* ==========================
         EMPTY
      ========================== */

      .empty-payment {
        margin: 0;

        padding: 15px;

        color: #5f6368;

        background: #f8f9fa;

        border-radius: 4px;

        text-align: center;

        font-size: 13px;
      }

      /* ==========================
         FOOTER
      ========================== */

      .payment-footer {
        padding: 12px 24px;

        border-top:
          1px solid #dadce0;

        display: flex;
        justify-content: flex-end;
      }

      .done-button {
        min-width: 85px;
        height: 38px;

        border: none;

        border-radius: 4px;

        background: ${PURPLE};
        color: white;

        padding: 0 20px;

        font-size: 14px;
        font-weight: 500;

        cursor: pointer;
      }

      .done-button:hover {
        background: #5e35b1;
      }

      /* ==========================
         MOBILE
      ========================== */

      @media (max-width: 600px) {
        .home-container {
          padding: 8px;
        }

        .banner-grid {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 6px;
        }

        .banner-card {
          border-radius: 8px;
        }

        .payment-overlay {
          padding: 0;

          align-items: flex-end;
        }

        .payment-dialog {
          max-height: 94vh;

          border-radius:
            8px 8px 0 0;
        }

        .payment-header {
          padding: 18px;
        }

        .response-success {
          margin:
            14px 18px 0;
        }

        .payment-body {
          padding: 18px;
        }

        .payment-footer {
          padding: 12px 18px;
        }

        .qr-image-box img {
          width: 200px;
        }
      }
    `}</style>
  );
}
